import { queryOptions, useQuery } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const WOOP_WORKLOADS_SUMMARY_QUERY_KEY = 'woop-workloads-summary';

const getWorkloadsOptimizationSummaryQueryKey = ({
  clusterId,
  tourStepId,
}: { clusterId?: string; tourStepId?: string | null } = {}) =>
  [WOOP_WORKLOADS_SUMMARY_QUERY_KEY, clusterId, tourStepId] as const;

type UseWorkloadsOptimizationSummaryQueryArgs = {
  clusterId: string;
  enabled?: boolean;
};

const getWorkloadsOptimizationSummaryQueryOptions = ({
  clusterId,
  tourStepId,
  enabled = true,
}: UseWorkloadsOptimizationSummaryQueryArgs & {
  tourStepId?: string | null;
}) =>
  queryOptions({
    queryKey: getWorkloadsOptimizationSummaryQueryKey({
      clusterId,
      tourStepId,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.workloadOptimization.getWorkloadsOptimizationSummary({
          clusterId,
          includeCosts: true,
        });
      return data;
    },
    enabled,
  });

export const useWorkloadsOptimizationSummaryQuery = (
  args: UseWorkloadsOptimizationSummaryQueryArgs
) => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const { isLoading, data, error, refetch, isRefetching } = useQuery(
    getWorkloadsOptimizationSummaryQueryOptions({ ...args, tourStepId })
  );

  return { isLoading: isLoading || isRefetching, error, refetch, data };
};
